import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, ShieldCheck, CalendarClock, CheckCircle2 } from 'lucide-react';
import { PaymentTermsCheck } from '../components/PaymentTermsCheck';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function RenewPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/renew/${token}`)
      .then((r) => { setInfo(r.data); setPaid(r.data.already_paid); })
      .catch((e) => setError(e.response?.data?.detail || 'This renewal link is not valid'));
  }, [token]);

  const fmt = (n) => (info?.currency === 'INR' ? `₹${Number(n).toLocaleString('en-IN')}` : `$${n}`);

  const pay = async () => {
    setPaying(true);
    setError('');
    try {
      if (!(await loadRazorpay())) throw new Error('Failed to load payment gateway');
      const { data } = await axios.post(`${API_URL}/api/renew/${token}/order`, { terms_accepted: termsAccepted });
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'BrandStage',
        description: data.product_name,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: '#C41E3A' },
        handler: async (resp) => {
          try {
            await axios.post(`${API_URL}/api/renew/${token}/verify`, resp);
            setPaid(true);
          } catch {
            setError('Payment received but verification failed. Our team will contact you shortly.');
          }
          setPaying(false);
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to start payment');
      setPaying(false);
    }
  };

  if (!info && !error) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-6 py-16" data-testid="renew-page">
      <div className="w-full max-w-lg">
        <Link to="/" className="block mb-10"><img src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png" alt="BrandStage" className="h-14 -ml-2" /></Link>
        {!info ? (
          <p className="text-red-400" data-testid="renew-error">{error}</p>
        ) : paid ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8" data-testid="renew-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-4" />
            <h1 className="text-3xl font-bold mb-2">Renewed. Thank you!</h1>
            <p className="text-zinc-400">Your {info.title} has been extended. A confirmation email is on its way.</p>
          </div>
        ) : (
          <>
            <p className="text-zinc-500 text-sm mb-2">Renewal for {info.company_name || info.customer_name}</p>
            <h1 className="text-4xl font-bold tracking-tight mb-3" data-testid="renew-title">{info.title}</h1>
            {info.current_expiry && (
              <p className="text-amber-400 text-sm flex items-center gap-2 mb-8"><CalendarClock className="w-4 h-4" /> Current period ends {new Date(info.current_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}. Your renewal starts from that date.</p>
            )}
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6">
              <p className="text-sm text-zinc-400 mb-3">What's included</p>
              <ul className="space-y-2 mb-6" data-testid="renew-includes">
                {info.includes.map((i) => <li key={i} className="flex items-start gap-2 text-sm text-zinc-200"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{i}</li>)}
              </ul>
              <div className="space-y-2 border-t border-zinc-800 pt-4 text-sm">
                {info.lines.map((l) => <div key={l.name} className="flex justify-between text-zinc-400"><span>{l.name}</span><span>{fmt(l.amount)}</span></div>)}
                {info.gst > 0 && <div className="flex justify-between text-zinc-400"><span>GST ({info.gst_rate}%)</span><span>{fmt(info.gst)}</span></div>}
                <div className="flex justify-between text-white font-bold text-lg pt-3 border-t border-zinc-800"><span>Total</span><span data-testid="renew-total">{fmt(info.total)}</span></div>
              </div>
              <div className="mt-6"><PaymentTermsCheck checked={termsAccepted} onChange={setTermsAccepted} testId="renew-terms-checkbox" /></div>
              <button type="button" onClick={pay} disabled={paying || !termsAccepted} data-testid="renew-pay-btn" className="w-full mt-6 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white py-3 rounded-full font-semibold flex items-center justify-center gap-2">
                {paying && <Loader2 className="w-4 h-4 animate-spin" />} Pay {fmt(info.total)} &amp; renew
              </button>
              {error && <p className="text-red-400 text-sm mt-3" data-testid="renew-error">{error}</p>}
              <p className="text-zinc-600 text-xs mt-4 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure payment powered by Razorpay. No login needed.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
